import { Fragment, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
  SortingState
} from "@tanstack/react-table";
import PaginationControls from "../boards/PaginationControls";
import { EpicRun } from "../../types/EpicRun";
import { getEpicColor } from "../../utils/epicColors";
import RunParticipants from "./RunParticipants";
import "./EpicRunsTable.css";

const PAGE_SIZE = 25;

const columnHelper = createColumnHelper<EpicRun>();

const columns = [
  columnHelper.accessor("organizer", {
    header: "Organizer",
    cell: (info) => info.getValue() || "-"
  }),
  columnHelper.accessor("epicName", {
    header: "Epic",
    cell: (info) => {
      const name = info.getValue();
      return (
        <span className="epic-badge">
          <span
            className="epic-dot"
            style={{ backgroundColor: getEpicColor(name) }}
          />
          {name || "-"}
        </span>
      );
    }
  }),
  columnHelper.accessor("runDate", {
    header: "Date",
    // ISO 8601 strings sort correctly lexicographically, no Date coercion needed
    cell: (info) => new Date(info.getValue()).toLocaleDateString()
  }),
  columnHelper.accessor((row) => new Date(row.runDate).getHours(), {
    id: "hour",
    header: "Finished At",
    cell: (info) => {
      const date = new Date(info.row.original.runDate);
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
  }),
  columnHelper.accessor("totalPlayers", {
    header: "Players"
  })
];

type Props = {
  data: EpicRun[];
};

const EpicRunsTable = ({ data }: Props) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "runDate", desc: true }
  ]);
  const [openRuns, setOpenRuns] = useState<Set<number>>(new Set());

  const toggleRun = (id: number) =>
    setOpenRuns((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } }
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pagination = {
    currentPage: pageIndex + 1,
    totalPages: Math.max(1, table.getPageCount()),
    totalCount: data.length,
    limit: pageSize,
    offset: pageIndex * pageSize,
    hasNextPage: table.getCanNextPage(),
    hasPreviousPage: table.getCanPreviousPage()
  };

  return (
    <div>
      <div className="epic-table-wrap">
        <table className="epic-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortDir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={header.column.getCanSort() ? "sortable" : ""}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {sortDir === "asc" && " ▲"}
                      {sortDir === "desc" && " ▼"}
                    </th>
                  );
                })}
                <th aria-label="Details" />
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  No results
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const run = row.original;
                const open = openRuns.has(run.id);
                const panelId = `epic-run-${run.id}`;
                return (
                  <Fragment key={row.id}>
                    <tr
                      className="epic-row"
                      onClick={() => toggleRun(run.id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                      <td className="epic-expand-cell">
                        <button
                          type="button"
                          className="epic-expand"
                          aria-expanded={open}
                          aria-controls={panelId}
                          aria-label={`${open ? "Hide" : "Show"} details for ${
                            run.epicName ?? "run"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRun(run.id);
                          }}
                        >
                          <span
                            className={`epic-expand-chevron${
                              open ? " epic-expand-chevron--open" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="epic-detail-row">
                        <td colSpan={columns.length + 1} id={panelId}>
                          <div className="epic-detail-times">
                            <span>
                              <span className="epic-detail-label">
                                Clear time
                              </span>{" "}
                              {run.totalTime || "-"}
                            </span>
                            {run.totalRealTime && (
                              <span>
                                <span className="epic-detail-label">
                                  Real time
                                </span>{" "}
                                {run.totalRealTime}
                              </span>
                            )}
                          </div>
                          <RunParticipants runId={run.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={(page) => table.setPageIndex(page - 1)}
        />
      )}
    </div>
  );
};

export default EpicRunsTable;
